import React from 'react';
import { Pressable, ScrollView, StyleSheet, View } from 'react-native';
import PageHeader from '../../components/account/PageHeader';
import Icon from '../../components/ui/Icon';
import Text from '../../components/ui/Text';
import { appVersion } from '../../services/appVersion';
import {
  colors,
  hairline,
  radii,
  screenPadding,
  spacing,
  useResponsive,
} from '../../theme';

/**
 * App settings - how the app behaves, not who you are.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS IS NOT PRIVACY, AND NOT PROFILE
 * ---------------------------------------------------------------------------
 * Two different questions were sharing one list. `PrivacyScreen` answers "who
 * can see me"; this answers "how should the app work". Language sitting next to
 * Delete My Account made both harder to find, and only one of the two is
 * dangerous to get wrong.
 *
 * ---------------------------------------------------------------------------
 * EVERY ROW HERE WORKS
 * ---------------------------------------------------------------------------
 * That is the rule this page is now held to, and it is why the page is short.
 *
 * It used to carry Language, Theme, Currency, Sounds & vibration and Data
 * saver. All five persisted to `homingo.settings` and survived a restart -
 * genuinely - but nothing in the app reads any of those keys: there is no
 * translation bundle, no second currency, no dark palette in `theme/colors`
 * and no sound bank. Each row admitted as much in muted type underneath
 * itself, which was treated as making it honest. It does not: a control that
 * flips and changes nothing is the one thing a settings page must never
 * contain, and a caption does not undo the flip. They are gone.
 *
 * "Terms of service" and "Rate Homingo" went with them - both were no-ops
 * wearing a chevron.
 *
 * What is left is two links to pages that exist and a version string read from
 * the build. Add a row back when the thing it configures is real.
 */






type Props = {
  onBack: () => void;
  /** Opens Privacy & Security, which owns the switches that are not here. */
  onOpenPrivacy: () => void;
  /** Opens Notifications, for the same reason. */
  onOpenNotifications: () => void;
};

function SettingsScreen({
  onBack,
  onOpenPrivacy,
  onOpenNotifications,
}: Props) {
  const resp = useResponsive();

  /*
   * No settings state left to hold.
   *
   * `loadSettings`/`saveSettings`, the `set` writer and the `chooser` row
   * builder all went with the Language, Theme and toggle rows above - they
   * persisted preferences that nothing in the app ever read. `AppSettings` and
   * the storage helpers are untouched and still exported, so re-adding a row
   * is re-adding the reader for it, not rebuilding the plumbing.
   */

  return (
    <View style={styles.root}>
      <PageHeader title="Settings" onBack={onBack} />

      {/* `flex: 1` on the scroller itself, not only on its content - inside a
          flex column a ScrollView with no flex of its own sizes to its content
          and overflows, cutting off the foot of the list. */}
      <ScrollView
        style={styles.scroller}
        /* Zero on a phone; on a tablet it holds the page to a readable column
           and centres it. See `theme/responsive.ts`. */
        contentContainerStyle={[styles.page, resp.page]}
        showsVerticalScrollIndicator={false}
      >
        {/*
          -------------------------------------------------------------------
          NOTHING HERE THAT DOES NOT WORK
          -------------------------------------------------------------------
          Two whole sections were removed rather than reworded.

          "Language & appearance" offered Language (en/hi/mr), Theme
          (system/light/dark) and Currency. "App behaviour" offered Sounds &
          vibration and Data saver. Each of the four controls wrote to
          AsyncStorage and survived a restart, so they LOOKED like working
          settings - but nothing in the app reads `settings.language`,
          `settings.theme`, `settings.sounds` or `settings.dataSaver`. There is
          one palette, one translation and no sound bank. Currency was not even
          pressable: a row displaying the only value it can have.

          Each row carried a line of small print admitting it did nothing
          ("dark is not built yet"). That is worse than absence, not better -
          a control that flips and changes nothing is the one thing a settings
          page must never contain, and small print does not undo the flip.

          `AppSettings` and `saveSettings` are untouched. The stored keys stay
          valid, so bringing any of these back is re-adding the row once the
          thing it configures exists.
        */}

        {/* ------------------------------------------------------ elsewhere */}
        {/*
          Pointers, not copies. Notification switches live on the Notifications
          page and the privacy switches on Privacy & Security; duplicating either
          here would give one setting two homes that can disagree.
        */}
        <Text variant="labelSm" color={colors.inkMuted}>
          Managed elsewhere
        </Text>

        <View style={styles.card}>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Notification preferences"
            onPress={onOpenNotifications}
            style={({ pressed }) => [styles.row, pressed ? styles.pressed : null]}
          >
            <View style={styles.mark}>
              <Icon name="bell" size={17} color={colors.primary} />
            </View>
            <View style={styles.body}>
              <Text variant="subtitle">Notifications</Text>
              <Text variant="caption" color={colors.inkMuted}>
                What Homingo is allowed to alert you about.
              </Text>
            </View>
            <Icon name="chevronRight" size={15} color={colors.borderStrong} />
          </Pressable>

          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Privacy and security"
            onPress={onOpenPrivacy}
            style={({ pressed }) => [
              styles.row,
              styles.divided,
              pressed ? styles.pressed : null,
            ]}
          >
            <View style={styles.mark}>
              <Icon name="shieldCheck" size={17} color={colors.primary} />
            </View>
            <View style={styles.body}>
              <Text variant="subtitle">Privacy & security</Text>
              <Text variant="caption" color={colors.inkMuted}>
                App lock, location sharing, your data and account deletion.
              </Text>
            </View>
            <Icon name="chevronRight" size={15} color={colors.borderStrong} />
          </Pressable>
        </View>

        {/* ---------------------------------------------------------- about */}
        <Text variant="labelSm" color={colors.inkMuted}>
          About
        </Text>

        <View style={styles.card}>
          <View style={styles.row}>
            <View style={styles.mark}>
              <Icon name="grid" size={17} color={colors.primary} />
            </View>
            <View style={styles.body}>
              <Text variant="subtitle">App version</Text>
              {/* Read from the build, not typed here. This was the literal
                  "Homingo 0.0.1 - pre-release build", which the file's own
                  header claimed was "the build you are running" - it was a
                  hand-typed string that would go stale at the next version
                  bump and say nothing useful in a bug report. */}
              <Text variant="caption" color={colors.inkMuted}>
                {`Homingo ${appVersion()}`}
              </Text>
            </View>
          </View>

          {/*
            "Terms of service" and "Rate Homingo" were here, both routed to
            `onUnavailable` - which for a signed-in customer is a literal
            no-op. They rendered a chevron and a pressed state, so they read as
            live links, and tapping either did nothing whatsoever. Signed out
            they were worse: the sign-in sheet came up, implying an account
            would unlock a document that is simply not written yet.

            Terms comes back when there is a URL to open. "Rate Homingo" comes
            back when the app is on a store - `services/appUpdate.ts` already
            holds the listing URLs for that day.
          */}
        </View>

        {/* The old footnote promised "your choices are saved on this device
            and survive a restart", which described the settings that have
            since been removed for not doing anything. What is left here are
            links and a version string, so there is nothing to reassure anyone
            about. */}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: colors.background,
  },
  scroller: {
    flex: 1,
  },
  page: {
    /* Vertical only: the horizontal gutter comes from `resp.page` on the
       contentContainer, which also carries the tablet inset. `padding`
       here was silently overridden by it. */
    paddingVertical: screenPadding,
    gap: spacing.sm,
    // Clears the floating tab bar, which overlays the bottom of the screen.
    paddingBottom: 120,
  },
  card: {
    borderRadius: radii.lg,
    backgroundColor: colors.surface,
    marginBottom: spacing.md,
    overflow: 'hidden',
    ...hairline,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    padding: spacing.md,
  },
  /* On the child rather than the card, so the first row has no rule above it. */
  divided: {
    borderTopWidth: hairline.borderWidth,
    borderTopColor: hairline.borderColor,
  },
  /* Indented past the mark, so an option reads as belonging to the row that
     opened it rather than as another row. */
  choice: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.sm,
    paddingRight: spacing.md,
    paddingLeft: spacing.md + 34 + spacing.md,
    backgroundColor: colors.surfaceAccent,
  },
  mark: {
    width: 34,
    height: 34,
    borderRadius: radii.sm,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surfaceAccent,
  },
  body: {
    flex: 1,
    gap: 2,
  },
  footnote: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.sm,
    paddingTop: spacing.xs,
  },
  grow: {
    flex: 1,
  },
  pressed: {
    opacity: 0.6,
  },
});

export default SettingsScreen;
